/**
 * `/tutorial` — the complete public guide (docs/PLAN_LANDING_V2.md F4, rewritten per docs/PLAN_TUTORIAL_V2.md).
 * Substance comes from the running product, re-checked against the code on every pass: chapter text quotes the
 * exact Indonesian button labels, and every limit named here (retention windows, guest visit length, nearby chat
 * lifetime, focus session bounds) is the limit the server actually enforces.
 *
 * Shape of every chapter: goal → numbered steps → "how you know it worked". That triple is the thing that
 * makes a manual usable. Chapters are grouped into seven parts so a 37-chapter page stays navigable; the
 * numbering stays continuous across parts, because "Bagian 24" is how people cite it to each other.
 * Layout: sticky table of contents on the left (`.mk-toc`), chapters on the right; single column below `lg`.
 *
 * Anchors `kantor`, `tamu-kantor`, `pencarian-pesan` and `calendar-companion` are linked from
 * `office-section.tsx` on the home page — renaming them breaks those links, so they stay.
 *
 * Deliberately NOT documented, because they are not usable today: `/models` (a ComingSoon placeholder),
 * Google Calendar OAuth and GitHub (both behind flags that are off in production), Spotify account linking
 * (`SPOTIFY_ENABLED=0`; the "Musik kerja bersama" link-sharing in the office is a different thing and is
 * documented), and AI knowledge search (planned, not built).
 */
import { m } from '#/paraglide/messages'
import { Container, MkFooter, MkLink, MkNav, Overline } from './mk'
import { Faq, PageHero, Rich, Section, Shot } from './mk-blocks'
import type { ShotId } from './shots'

type Chapter = {
  id: string
  /** 1-based index into PARTS */
  part: number
  title: () => string
  goal: () => string
  steps?: Array<() => string>
  success?: () => string
  note?: () => string
  shot?: ShotId
  action?: { href: string; label: () => string }
}

const PARTS: Array<{ title: () => string; desc: () => string }> = [
  { title: () => m.tut_part_1_t(), desc: () => m.tut_part_1_d() },
  { title: () => m.tut_part_2_t(), desc: () => m.tut_part_2_d() },
  { title: () => m.tut_part_3_t(), desc: () => m.tut_part_3_d() },
  { title: () => m.tut_part_4_t(), desc: () => m.tut_part_4_d() },
  { title: () => m.tut_part_5_t(), desc: () => m.tut_part_5_d() },
  { title: () => m.tut_part_6_t(), desc: () => m.tut_part_6_d() },
  { title: () => m.tut_part_7_t(), desc: () => m.tut_part_7_d() },
]

const CHAPTERS: Chapter[] = [
  // ---------------------------------------------------------------- 1. Mulai
  {
    id: 'apa',
    part: 1,
    title: () => m.mk_tut_apa_t(),
    goal: () => m.mk_tut_apa_g(),
    steps: [
      () => m.mk_tut_apa_s1(),
      () => m.mk_tut_apa_s2(),
      () => m.mk_tut_apa_s3(),
      () => m.mk_tut_apa_s4(),
      () => m.tut_apa_s5(),
      () => m.tut_apa_s6(),
    ],
    note: () => m.mk_tut_apa_n(),
  },
  {
    id: 'daftar',
    part: 1,
    title: () => m.mk_tut_daftar_t(),
    goal: () => m.mk_tut_daftar_g(),
    steps: [() => m.mk_tut_daftar_s1(), () => m.mk_tut_daftar_s2(), () => m.mk_tut_daftar_s3()],
    success: () => m.mk_tut_daftar_ok(),
  },
  {
    id: 'workspace',
    part: 1,
    title: () => m.mk_tut_ws_t(),
    goal: () => m.mk_tut_ws_g(),
    steps: [() => m.mk_tut_ws_s1(), () => m.mk_tut_ws_s2(), () => m.mk_tut_ws_s3(), () => m.mk_tut_ws_s4(), () => m.mk_tut_ws_s5()],
    success: () => m.mk_tut_ws_ok(),
    note: () => m.mk_tut_ws_n(),
  },
  {
    id: 'tim',
    part: 1,
    title: () => m.mk_tut_tim_t(),
    goal: () => m.mk_tut_tim_g(),
    steps: [() => m.mk_tut_tim_s1(), () => m.mk_tut_tim_s2(), () => m.mk_tut_tim_s3()],
    success: () => m.mk_tut_tim_ok(),
    note: () => m.mk_tut_tim_n(),
  },

  // -------------------------------------------------------- 2. Kerja harian
  {
    id: 'issue',
    part: 2,
    title: () => m.mk_tut_issue_t(),
    goal: () => m.mk_tut_issue_g(),
    steps: [() => m.mk_tut_issue_s1(), () => m.mk_tut_issue_s2(), () => m.mk_tut_issue_s3(), () => m.mk_tut_issue_s4(), () => m.mk_tut_issue_s5()],
    success: () => m.mk_tut_issue_ok(),
    shot: 'board',
  },
  {
    id: 'proyek',
    part: 2,
    title: () => m.tut_proyek_t(),
    goal: () => m.tut_proyek_g(),
    steps: [() => m.tut_proyek_s1(), () => m.tut_proyek_s2(), () => m.tut_proyek_s3(), () => m.tut_proyek_s4()],
    success: () => m.tut_proyek_ok(),
    note: () => m.tut_proyek_n(),
    shot: 'list',
  },
  {
    id: 'chat',
    part: 2,
    title: () => m.mk_tut_chat_t(),
    goal: () => m.mk_tut_chat_g(),
    steps: [() => m.mk_tut_chat_s1(), () => m.mk_tut_chat_s2(), () => m.mk_tut_chat_s3(), () => m.mk_tut_chat_s4()],
    success: () => m.mk_tut_chat_ok(),
    shot: 'chat',
  },
  {
    id: 'inbox',
    part: 2,
    title: () => m.tut_inbox_t(),
    goal: () => m.tut_inbox_g(),
    steps: [() => m.tut_inbox_s1(), () => m.tut_inbox_s2(), () => m.tut_inbox_s3(), () => m.tut_inbox_s4()],
    success: () => m.tut_inbox_ok(),
    note: () => m.tut_inbox_n(),
    shot: 'inbox',
  },
  {
    id: 'pencarian-pesan',
    part: 2,
    title: () => m.lg_search_guide_title(),
    goal: () => m.lg_search_goal(),
    steps: [() => m.lg_search_s1(), () => m.lg_search_s2(), () => m.lg_search_s3()],
    success: () => m.lg_search_ok(),
    note: () => m.lg_search_note(),
  },
  {
    id: 'dokumen',
    part: 2,
    title: () => m.tut_dok_t(),
    goal: () => m.tut_dok_g(),
    steps: [
      () => m.tut_dok_s1(),
      () => m.tut_dok_s2(),
      () => m.tut_dok_s3(),
      () => m.tut_dok_s4(),
      () => m.tut_dok_s5(),
      () => m.tut_dok_s6(),
    ],
    success: () => m.tut_dok_ok(),
    note: () => m.tut_dok_n(),
    shot: 'document',
  },
  {
    id: 'papan-tulis',
    part: 2,
    title: () => m.tut_wb_t(),
    goal: () => m.tut_wb_g(),
    steps: [() => m.tut_wb_s1(), () => m.tut_wb_s2(), () => m.tut_wb_s3(), () => m.tut_wb_s4(), () => m.tut_wb_s5()],
    success: () => m.tut_wb_ok(),
    note: () => m.tut_wb_n(),
  },

  // ------------------------------------------------------ 3. Kantor virtual
  {
    id: 'kantor',
    part: 3,
    title: () => m.tut_kantor_t(),
    goal: () => m.tut_kantor_g(),
    steps: [() => m.tut_kantor_s1(), () => m.tut_kantor_s2(), () => m.tut_kantor_s3(), () => m.tut_kantor_s4(), () => m.tut_kantor_s5()],
    success: () => m.tut_kantor_ok(),
    note: () => m.tut_kantor_n(),
    shot: 'office',
  },
  {
    id: 'kantor-denah',
    part: 3,
    title: () => m.tut_denah_t(),
    goal: () => m.tut_denah_g(),
    steps: [() => m.tut_denah_s1(), () => m.tut_denah_s2(), () => m.tut_denah_s3(), () => m.tut_denah_s4()],
    success: () => m.tut_denah_ok(),
    note: () => m.tut_denah_n(),
  },
  {
    id: 'kantor-akses',
    part: 3,
    title: () => m.tut_akses_t(),
    goal: () => m.tut_akses_g(),
    steps: [() => m.tut_akses_s1(), () => m.tut_akses_s2(), () => m.tut_akses_s3(), () => m.tut_akses_s4()],
    success: () => m.tut_akses_ok(),
    note: () => m.tut_akses_n(),
  },
  {
    id: 'kantor-meja',
    part: 3,
    title: () => m.tut_meja_t(),
    goal: () => m.tut_meja_g(),
    steps: [() => m.tut_meja_s1(), () => m.tut_meja_s2(), () => m.tut_meja_s3(), () => m.tut_meja_s4()],
    success: () => m.tut_meja_ok(),
    note: () => m.tut_meja_n(),
  },
  {
    id: 'kantor-audio',
    part: 3,
    title: () => m.tut_audio_t(),
    goal: () => m.tut_audio_g(),
    steps: [() => m.tut_audio_s1(), () => m.tut_audio_s2(), () => m.tut_audio_s3()],
    success: () => m.tut_audio_ok(),
    note: () => m.tut_audio_n(),
  },
  {
    id: 'kantor-fokus',
    part: 3,
    title: () => m.tut_fokus_t(),
    goal: () => m.tut_fokus_g(),
    steps: [() => m.tut_fokus_s1(), () => m.tut_fokus_s2(), () => m.tut_fokus_s3(), () => m.tut_fokus_s4()],
    success: () => m.tut_fokus_ok(),
    note: () => m.tut_fokus_n(),
  },
  {
    id: 'kantor-santai',
    part: 3,
    title: () => m.tut_santai_t(),
    goal: () => m.tut_santai_g(),
    steps: [() => m.tut_santai_s1(), () => m.tut_santai_s2(), () => m.tut_santai_s3(), () => m.tut_santai_s4()],
    success: () => m.tut_santai_ok(),
    note: () => m.tut_santai_n(),
  },
  {
    id: 'kantor-cari',
    part: 3,
    title: () => m.tut_oscari_t(),
    goal: () => m.tut_oscari_g(),
    steps: [() => m.tut_oscari_s1(), () => m.tut_oscari_s2(), () => m.tut_oscari_s3()],
    success: () => m.tut_oscari_ok(),
    note: () => m.tut_oscari_n(),
  },
  {
    id: 'kantor-mini',
    part: 3,
    title: () => m.tut_mini_t(),
    goal: () => m.tut_mini_g(),
    steps: [() => m.tut_mini_s1(), () => m.tut_mini_s2(), () => m.tut_mini_s3()],
    success: () => m.tut_mini_ok(),
    note: () => m.tut_mini_n(),
  },
  {
    id: 'tamu-kantor',
    part: 3,
    title: () => m.lg_guest_guide_title(),
    goal: () => m.lg_guest_goal(),
    steps: [() => m.lg_guest_s1(), () => m.lg_guest_s2(), () => m.lg_guest_s3()],
    success: () => m.lg_guest_ok(),
    note: () => m.lg_guest_note(),
  },

  // --------------------------------------------------------------- 4. Rapat
  {
    id: 'rapat',
    part: 4,
    title: () => m.tut_rapat_t(),
    goal: () => m.tut_rapat_g(),
    steps: [() => m.tut_rapat_s1(), () => m.tut_rapat_s2(), () => m.tut_rapat_s3(), () => m.tut_rapat_s4(), () => m.tut_rapat_s5()],
    success: () => m.tut_rapat_ok(),
    note: () => m.tut_rapat_n(),
    shot: 'meetings',
  },
  {
    id: 'rapat-jadwal',
    part: 4,
    title: () => m.tut_jadwal_t(),
    goal: () => m.tut_jadwal_g(),
    steps: [() => m.tut_jadwal_s1(), () => m.tut_jadwal_s2(), () => m.tut_jadwal_s3(), () => m.tut_jadwal_s4()],
    success: () => m.tut_jadwal_ok(),
    note: () => m.tut_jadwal_n(),
  },
  {
    id: 'rapat-rekaman',
    part: 4,
    title: () => m.tut_rekam_t(),
    goal: () => m.tut_rekam_g(),
    steps: [() => m.tut_rekam_s1(), () => m.tut_rekam_s2(), () => m.tut_rekam_s3(), () => m.tut_rekam_s4()],
    success: () => m.tut_rekam_ok(),
    note: () => m.tut_rekam_n(),
  },
  {
    id: 'rapat-draf',
    part: 4,
    title: () => m.tut_draf_t(),
    goal: () => m.tut_draf_g(),
    steps: [() => m.tut_draf_s1(), () => m.tut_draf_s2(), () => m.tut_draf_s3(), () => m.tut_draf_s4()],
    success: () => m.tut_draf_ok(),
    note: () => m.tut_draf_n(),
  },
  {
    id: 'calendar-companion',
    part: 4,
    title: () => m.lg_companion_title(),
    goal: () => m.lg_companion_goal(),
    steps: [() => m.tut_kal_s0(), () => m.lg_companion_s1(), () => m.lg_companion_s2(), () => m.lg_companion_s3()],
    note: () => m.lg_companion_note(),
    action: { href: '/calendar-companion', label: () => m.lg_companion_action() },
  },

  // ------------------------------------------------------- 5. Rekan kerja AI
  {
    id: 'agen',
    part: 5,
    title: () => m.mk_tut_agen_t(),
    goal: () => m.mk_tut_agen_g(),
    steps: [() => m.mk_tut_agen_s1(), () => m.mk_tut_agen_s2(), () => m.mk_tut_agen_s3(), () => m.mk_tut_agen_s4()],
    success: () => m.mk_tut_agen_ok(),
    note: () => m.mk_tut_agen_n(),
    shot: 'issue',
  },
  {
    id: 'approval',
    part: 5,
    title: () => m.mk_tut_apr_t(),
    goal: () => m.mk_tut_apr_g(),
    steps: [() => m.mk_tut_apr_s1(), () => m.mk_tut_apr_s2(), () => m.mk_tut_apr_s3()],
    success: () => m.mk_tut_apr_ok(),
    note: () => m.mk_tut_apr_n(),
    shot: 'approvals',
  },
  {
    id: 'memori',
    part: 5,
    title: () => m.mk_tut_mem_t(),
    goal: () => m.mk_tut_mem_g(),
    steps: [() => m.mk_tut_mem_s1(), () => m.mk_tut_mem_s2(), () => m.mk_tut_mem_s3()],
    success: () => m.mk_tut_mem_ok(),
    note: () => m.mk_tut_mem_n(),
    shot: 'memory',
  },
  {
    id: 'rutinitas',
    part: 5,
    title: () => m.mk_tut_rut_t(),
    goal: () => m.mk_tut_rut_g(),
    steps: [() => m.mk_tut_rut_s1(), () => m.mk_tut_rut_s2(), () => m.mk_tut_rut_s3()],
    success: () => m.mk_tut_rut_ok(),
    shot: 'autopilots',
  },
  {
    id: 'skill',
    part: 5,
    title: () => m.mk_tut_skill_t(),
    goal: () => m.mk_tut_skill_g(),
    steps: [() => m.mk_tut_skill_s1(), () => m.mk_tut_skill_s2(), () => m.mk_tut_skill_s3()],
    success: () => m.mk_tut_skill_ok(),
    shot: 'skills',
  },

  // ------------------------------------------- 6. Tim tak pernah benar-benar offline
  {
    id: 'presence',
    part: 6,
    title: () => m.tut_presence_t(),
    goal: () => m.tut_presence_g(),
    steps: [() => m.tut_presence_s1(), () => m.tut_presence_s2(), () => m.tut_presence_s3(), () => m.tut_presence_s4()],
    success: () => m.tut_presence_ok(),
    note: () => m.tut_presence_n(),
  },
  {
    id: 'wakil-digital',
    part: 6,
    title: () => m.tut_wakil_t(),
    goal: () => m.tut_wakil_g(),
    steps: [() => m.tut_wakil_s1(), () => m.tut_wakil_s2(), () => m.tut_wakil_s3(), () => m.tut_wakil_s4()],
    success: () => m.tut_wakil_ok(),
    note: () => m.tut_wakil_n(),
  },
  {
    id: 'selagi-pergi',
    part: 6,
    title: () => m.tut_pergi_t(),
    goal: () => m.tut_pergi_g(),
    steps: [() => m.tut_pergi_s1(), () => m.tut_pergi_s2(), () => m.tut_pergi_s3()],
    success: () => m.tut_pergi_ok(),
    note: () => m.tut_pergi_n(),
  },

  // ------------------------------------------------------------ 7. Mengatur
  {
    id: 'tampilan',
    part: 7,
    title: () => m.tut_tampilan_t(),
    goal: () => m.tut_tampilan_g(),
    steps: [() => m.tut_tampilan_s1(), () => m.tut_tampilan_s2(), () => m.tut_tampilan_s3(), () => m.tut_tampilan_s4()],
    success: () => m.tut_tampilan_ok(),
    note: () => m.tut_tampilan_n(),
  },
  {
    id: 'biaya',
    part: 7,
    title: () => m.mk_tut_biaya_t(),
    goal: () => m.mk_tut_biaya_g(),
    steps: [() => m.mk_tut_biaya_s1(), () => m.mk_tut_biaya_s2(), () => m.mk_tut_biaya_s3()],
    success: () => m.mk_tut_biaya_ok(),
    note: () => m.mk_tut_biaya_n(),
    shot: 'usage',
  },
  {
    id: 'paket',
    part: 7,
    title: () => m.tut_paket_t(),
    goal: () => m.tut_paket_g(),
    steps: [() => m.tut_paket_s1(), () => m.tut_paket_s2(), () => m.tut_paket_s3(), () => m.tut_paket_s4()],
    success: () => m.tut_paket_ok(),
    note: () => m.tut_paket_n(),
  },
]

/**
 * The real bindings, read off `src/components/shell/shortcuts.tsx` and the `chord` fields in
 * `src/components/shell/nav.ts`. An earlier version of this page listed `G I` for Issues and `G N` for the
 * Inbox; neither exists. Keep this table in step with `NAV_PAGES` when a chord is added or moved.
 */
const SHORTCUTS: Array<{ keys: string; what: () => string }> = [
  { keys: '⌘K / Ctrl+K', what: () => m.mk_tut_sc_palette() },
  { keys: 'C', what: () => m.mk_tut_sc_new() },
  { keys: '[', what: () => m.tut_sc_sidebar() },
  { keys: '?', what: () => m.tut_sc_help() },
  { keys: 'G lalu I', what: () => m.mk_tut_sc_inbox() },
  { keys: 'G lalu V', what: () => m.tut_sc_approvals() },
  { keys: 'G lalu M', what: () => m.tut_sc_mine() },
  { keys: 'G lalu L', what: () => m.mk_tut_sc_issues() },
  { keys: 'G lalu P', what: () => m.tut_sc_projects() },
  { keys: 'G lalu C', what: () => m.tut_sc_chat() },
  { keys: 'G lalu A', what: () => m.mk_tut_sc_agents() },
  { keys: 'G lalu Q', what: () => m.tut_sc_squads() },
  { keys: 'G lalu R', what: () => m.tut_sc_routines() },
  { keys: 'G lalu K', what: () => m.tut_sc_skills() },
  { keys: 'G lalu U', what: () => m.tut_sc_usage() },
  { keys: 'G lalu E', what: () => m.tut_sc_memory() },
  { keys: 'G lalu S', what: () => m.tut_sc_settings() },
  { keys: '⌘⏎ / Ctrl+Enter', what: () => m.mk_tut_sc_submit() },
  { keys: 'Esc', what: () => m.mk_tut_sc_close() },
]

const GLOSSARY: Array<{ term: () => string; def: () => string }> = [
  { term: () => m.mk_tut_gl_1_t(), def: () => m.mk_tut_gl_1_d() },
  { term: () => m.mk_tut_gl_2_t(), def: () => m.mk_tut_gl_2_d() },
  { term: () => m.mk_tut_gl_3_t(), def: () => m.mk_tut_gl_3_d() },
  { term: () => m.mk_tut_gl_4_t(), def: () => m.mk_tut_gl_4_d() },
  { term: () => m.mk_tut_gl_5_t(), def: () => m.mk_tut_gl_5_d() },
  { term: () => m.mk_tut_gl_6_t(), def: () => m.mk_tut_gl_6_d() },
  { term: () => m.mk_tut_gl_7_t(), def: () => m.mk_tut_gl_7_d() },
  { term: () => m.mk_tut_gl_8_t(), def: () => m.mk_tut_gl_8_d() },
  { term: () => m.tut_gl_9_t(), def: () => m.tut_gl_9_d() },
  { term: () => m.tut_gl_10_t(), def: () => m.tut_gl_10_d() },
  { term: () => m.tut_gl_11_t(), def: () => m.tut_gl_11_d() },
  { term: () => m.tut_gl_12_t(), def: () => m.tut_gl_12_d() },
  { term: () => m.tut_gl_13_t(), def: () => m.tut_gl_13_d() },
  { term: () => m.tut_gl_14_t(), def: () => m.tut_gl_14_d() },
  { term: () => m.tut_gl_15_t(), def: () => m.tut_gl_15_d() },
]

const TROUBLE: Array<{ q: () => string; a: () => string }> = [
  { q: () => m.mk_tut_tr_1_q(), a: () => m.mk_tut_tr_1_a() },
  { q: () => m.mk_tut_tr_2_q(), a: () => m.mk_tut_tr_2_a() },
  { q: () => m.mk_tut_tr_3_q(), a: () => m.mk_tut_tr_3_a() },
  { q: () => m.mk_tut_tr_4_q(), a: () => m.mk_tut_tr_4_a() },
  { q: () => m.mk_tut_tr_5_q(), a: () => m.mk_tut_tr_5_a() },
  { q: () => m.mk_tut_tr_6_q(), a: () => m.mk_tut_tr_6_a() },
  { q: () => m.tut_tr_7_q(), a: () => m.tut_tr_7_a() },
  { q: () => m.tut_tr_8_q(), a: () => m.tut_tr_8_a() },
  { q: () => m.tut_tr_9_q(), a: () => m.tut_tr_9_a() },
  { q: () => m.tut_tr_10_q(), a: () => m.tut_tr_10_a() },
  { q: () => m.tut_tr_11_q(), a: () => m.tut_tr_11_a() },
  { q: () => m.tut_tr_12_q(), a: () => m.tut_tr_12_a() },
]

function ChapterBlock({ c, n, first }: { c: Chapter; n: number; first?: boolean }) {
  return (
    <section id={c.id} className={first ? 'scroll-mt-24' : 'scroll-mt-24 border-t border-[var(--mk-border)] pt-12'}>
      <Overline className="text-[13px]!">{m.mk_tut_chapter({ n: String(n) })}</Overline>
      <h3 className="mt-3 max-w-[26ch] text-3xl!">{c.title()}</h3>
      <p className="mk-prose mt-4 max-w-[68ch]">
        <Rich>{c.goal()}</Rich>
      </p>
      {c.steps?.length ? (
        <ol className="mt-6 flex max-w-[68ch] list-decimal flex-col gap-3 pl-5">
          {c.steps.map((s) => (
            <li key={s()} className="mk-prose pl-1">
              <Rich>{s()}</Rich>
            </li>
          ))}
        </ol>
      ) : null}
      {c.success ? (
        <p className="mk-card mt-6 max-w-[68ch] px-5 py-4 text-[16px] leading-relaxed text-[var(--mk-fg)]">
          <strong className="font-semibold">{m.mk_tut_success_label()}</strong> <Rich>{c.success()}</Rich>
        </p>
      ) : null}
      {c.note ? (
        <p className="mk-muted mt-4 max-w-[68ch] text-[15px] leading-relaxed">
          <Rich>{c.note()}</Rich>
        </p>
      ) : null}
      {c.action ? (
        <a href={c.action.href} rel="noreferrer" className="mk-btn mk-btn-secondary mt-5 h-auto min-h-11 whitespace-normal">
          {c.action.label()}
        </a>
      ) : null}
      {c.shot ? <Shot id={c.shot} className="mt-8" /> : null}
    </section>
  )
}

function PartHeading({ n, title, desc }: { n: number; title: string; desc: string }) {
  return (
    <header className="border-t-2 border-[var(--mk-fg)] pt-6">
      <Overline className="text-[13px]!">{m.tut_part_label({ n: String(n) })}</Overline>
      <h2 className="mt-2 max-w-[24ch] text-2xl!">{title}</h2>
      <p className="mk-muted mt-2 max-w-[68ch] text-[15px] leading-relaxed">{desc}</p>
    </header>
  )
}

export function TutorialPage() {
  // Chapters are grouped for rendering but keep one running number across the whole page.
  let running = 0
  const groups = PARTS.map((part, i) => ({
    part,
    n: i + 1,
    chapters: CHAPTERS.filter((c) => c.part === i + 1).map((c) => ({ c, n: ++running })),
  }))

  return (
    <div className="mk h-full overflow-x-hidden overflow-y-auto scroll-smooth">
      <MkNav />
      <main id="mk-content" tabIndex={-1}>
        <PageHero
          overline={m.mk_tut_overline()}
          title={m.mk_tut_title()}
          lead={m.mk_tut_lead()}
          primary={m.mk_tut_cta_primary()}
          secondary={m.mk_tut_cta_secondary()}
          secondaryTo="/pricing"
          note={m.mk_tut_note()}
        />

        <section className="pb-24 lg:pb-32">
          <Container>
            <div className="grid gap-12 lg:grid-cols-[16rem_minmax(0,1fr)] lg:gap-16">
              <nav className="mk-toc hidden lg:block" aria-label={m.mk_tut_toc()}>
                <p className="mb-3 text-[13px] tracking-[0.04em] text-[var(--mk-fg-faint)] uppercase">{m.mk_tut_toc()}</p>
                {groups.map(({ part, n, chapters }) => (
                  <div key={n} className="mb-4">
                    <p className="mt-3 mb-1 text-[13px] font-medium text-[var(--mk-fg)]">{part.title()}</p>
                    {chapters.map(({ c, n: cn }) => (
                      <a key={c.id} href={`#${c.id}`}>
                        {cn}. {c.title()}
                      </a>
                    ))}
                  </div>
                ))}
                <div className="mt-4 border-t border-[var(--mk-border)] pt-3">
                  <a href="#pintasan">{m.mk_tut_sc_title()}</a>
                  <a href="#istilah">{m.mk_tut_gl_title()}</a>
                  <a href="#masalah">{m.mk_tut_tr_title()}</a>
                </div>
              </nav>

              <div className="flex min-w-0 flex-col gap-12">
                {groups.map(({ part, n, chapters }) => (
                  <div key={n} className="flex flex-col gap-12">
                    <PartHeading n={n} title={part.title()} desc={part.desc()} />
                    {chapters.map(({ c, n: cn }, ci) => (
                      <ChapterBlock key={c.id} c={c} n={cn} first={ci === 0} />
                    ))}
                  </div>
                ))}

                {/* keyboard shortcuts */}
                <section id="pintasan" className="scroll-mt-24 border-t-2 border-[var(--mk-fg)] pt-10">
                  <h2 className="max-w-[24ch] text-2xl!">{m.mk_tut_sc_title()}</h2>
                  <p className="mk-prose mt-4 max-w-[68ch]"><Rich>{m.mk_tut_sc_lead()}</Rich></p>
                  <dl className="mk-card-white mt-6 max-w-[68ch] p-2">
                    {SHORTCUTS.map((s) => (
                      <div key={s.keys} className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--mk-border)] px-4 py-3 last:border-b-0">
                        <dt>
                          <span className="mk-kbd">{s.keys}</span>
                        </dt>
                        <dd className="text-[15px] text-[var(--mk-fg-muted)]">{s.what()}</dd>
                      </div>
                    ))}
                  </dl>
                  <p className="mk-muted mt-4 max-w-[68ch] text-[15px] leading-relaxed">
                    <Rich>{m.tut_sc_note()}</Rich>
                  </p>
                </section>

                {/* glossary */}
                <section id="istilah" className="scroll-mt-24 border-t-2 border-[var(--mk-fg)] pt-10">
                  <h2 className="max-w-[24ch] text-2xl!">{m.mk_tut_gl_title()}</h2>
                  <p className="mk-prose mt-4 max-w-[68ch]"><Rich>{m.mk_tut_gl_lead()}</Rich></p>
                  <dl className="mt-6 grid max-w-[68ch] gap-4 sm:grid-cols-2">
                    {GLOSSARY.map((g) => (
                      <div key={g.term()} className="mk-card-white p-5">
                        <dt className="text-[16px] font-medium text-[var(--mk-fg)]">{g.term()}</dt>
                        <dd className="mk-muted mt-1.5 text-[15px] leading-relaxed">{g.def()}</dd>
                      </div>
                    ))}
                  </dl>
                </section>

                {/* troubleshooting */}
                <section id="masalah" className="scroll-mt-24 border-t-2 border-[var(--mk-fg)] pt-10">
                  <h2 className="max-w-[24ch] text-2xl!">{m.mk_tut_tr_title()}</h2>
                  <p className="mk-prose mt-4 max-w-[68ch]"><Rich>{m.mk_tut_tr_lead()}</Rich></p>
                  <Faq className="mt-6 mr-auto ml-0 max-w-[68ch]" items={TROUBLE.map((t) => ({ q: t.q(), a: t.a() }))} />
                </section>

                <div className="flex flex-wrap gap-3 border-t border-[var(--mk-border)] pt-10">
                  <MkLink to="/home">{m.mk_tut_cta_primary()}</MkLink>
                  <MkLink to="/pricing" variant="secondary" arrow>
                    {m.mk_tut_cta_secondary()}
                  </MkLink>
                </div>
              </div>
            </div>
          </Container>
        </section>

        <Section tone="dark" className="py-20 lg:py-24">
          <div className="mx-auto flex max-w-3xl flex-col items-center gap-6 text-center">
            <h2 className="max-w-[20ch]">{m.mk_tut_end_title()}</h2>
            <p className="mk-lead max-w-[52ch]">{m.mk_tut_end_lead()}</p>
            <div className="flex flex-wrap justify-center gap-3">
              <MkLink to="/home">{m.mk_tut_cta_primary()}</MkLink>
              <MkLink to="/keamanan" variant="secondary" arrow>
                {m.mk_nav_r_security()}
              </MkLink>
            </div>
          </div>
        </Section>
      </main>
      <MkFooter />
    </div>
  )
}
